import React, { useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import './SubmitAuthenticationPopup.css';

const SubmitAuthenticationPopup = ({
    visible,
    drivers,
    onClose,
    onAddToCart,
    onFileUpload,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [showCustomSection, setShowCustomSection] = useState(false);
    const [customDriver, setCustomDriver] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const fileInputRef = useRef(null);

    const handleAddToCart = () => {
        if (!uploadedImage) {
            setErrorMessage('Enter a driver name and upload an image of the autograph before adding to cart');
            return;
        }

        let driverToAdd = null;

        if (showCustomSection) {
            const selectedRadio = document.querySelector('input[name="predefinedDriver"]:checked');
            if (selectedRadio) {
                driverToAdd = {
                    fullName: selectedRadio.value,
                    authenticationPrice: 25,
                    uploadedImage: `data:${uploadedImage.mimeType};base64,${uploadedImage.base64}`, // Include the file object
                };
            } else if (customDriver) {
                driverToAdd = {
                    fullName: customDriver,
                    authenticationPrice: 15,
                    uploadedImage: `data:${uploadedImage.mimeType};base64,${uploadedImage.base64}`, // Include the file object
                };
            }
        } else if (selectedDriver) {
            driverToAdd = {
                fullName: selectedDriver.fullName,
                authenticationPrice: selectedDriver.authenticationPrice,
                uploadedImage: `data:${uploadedImage.mimeType};base64,${uploadedImage.base64}`, // Include the file object
            };
        }

        if (!driverToAdd) {
            setErrorMessage('Enter a driver name and upload an image of the autograph before adding to cart');
            return;
        }

        onAddToCart(driverToAdd);
        resetPopup();
    };

    const resetPopup = () => {
        setSearchTerm('');
        setSelectedDriver(null);
        setShowCustomSection(false);
        setCustomDriver('');
        setUploadedImage(null);
        setErrorMessage(''); // Clear the error message
        const radioButtons = document.getElementsByName('predefinedDriver');
        radioButtons.forEach((radio) => (radio.checked = false));
        onClose();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64String = e.target.result.split(',')[1];
                const mimeType = e.target.result.split(';')[0].split(':')[1]; // Extract MIME type
                setUploadedImage({ base64: base64String, mimeType });
                onFileUpload({ base64: base64String, mimeType });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragOver(false);
        const file = event.dataTransfer.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64String = e.target.result.split(',')[1];
                const mimeType = e.target.result.split(';')[0].split(':')[1]; // Extract MIME type
                setUploadedImage({ base64: base64String, mimeType });
                onFileUpload({ base64: base64String, mimeType });
            };
            reader.readAsDataURL(file);
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // Reset file input value
            fileInputRef.current.click(); // Trigger file input click
        }
    };

    return visible ? (
        <div className="driver-popup-overlay">
            <div className="driver-popup-content">
                {!showCustomSection && (
                    <>
                        <label htmlFor="driverDropdown">Select Driver</label>
                        <label className="driver-popup-search-message">
                            Start Typing to Search for a Driver
                        </label>
                        <Autocomplete
                            disablePortal
                            options={drivers}
                            getOptionLabel={(option) =>
                                `${option.fullName} - ($${option.authenticationPrice.toFixed(2)})`
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedDriver}
                            onChange={(event, newValue) => setSelectedDriver(newValue)}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ marginBottom: 2 }}
                        />
                    </>
                )}
                <div className="driver-popup-checkbox-container">
                    <label htmlFor="custom-driver-checkbox" className="driver-popup-checkbox-label">
                        <input
                            type="checkbox"
                            id="custom-driver-checkbox"
                            checked={showCustomSection}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                setShowCustomSection(isChecked); // Update the checkbox state
                                setCustomDriver(''); // Clear the customDriver input
                                if (!isChecked) {
                                    const radioButtons = document.getElementsByName('predefinedDriver');
                                    radioButtons.forEach((radio) => (radio.checked = false)); // Clear radio buttons
                                }
                            }}
                        />
                        Cannot find driver in the list
                    </label>
                </div>
                {showCustomSection && (
                    <div>
                        <label htmlFor="customDriver" className="driver-popup-label">Driver Name - ($15.00)</label>
                        <input
                            id="customDriver"
                            type="text"
                            value={customDriver}
                            onChange={(e) => {
                                setCustomDriver(e.target.value);

                                // Clear radio buttons when the user types
                                const radioButtons = document.getElementsByName('predefinedDriver');
                                radioButtons.forEach((radio) => (radio.checked = false));
                            }}
                            placeholder="Enter driver name..."
                            className="driver-popup-input"
                        />
                        <div className="driver-popup-radio-group">
                            <label className="driver-popup-radio-label">
                                <input
                                    type="radio"
                                    name="predefinedDriver"
                                    value="Jeff Gordon"
                                    onChange={() => setCustomDriver('')}
                                />
                                Jeff Gordon - ($25.00)
                            </label>
                            <label className="driver-popup-radio-label">
                                <input
                                    type="radio"
                                    name="predefinedDriver"
                                    value="Dale Earnhardt Sr"
                                    onChange={() => setCustomDriver('')}
                                />
                                Dale Earnhardt Sr - ($25.00)
                            </label>
                            <label className="driver-popup-radio-label">
                                <input
                                    type="radio"
                                    name="predefinedDriver"
                                    value="Richard Petty"
                                    onChange={() => setCustomDriver('')}
                                />
                                Richard Petty - ($25.00)
                            </label>
                        </div>
                    </div>
                )}
                <div
                    className={`driver-popup-drag-drop ${dragOver ? 'drag-over' : ''}`}
                    onClick={!uploadedImage ? openFilePicker : undefined}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {uploadedImage ? (
                        <div className="driver-popup-uploaded-image-container">
                            <img
                                src={`data:${uploadedImage.mimeType};base64,${uploadedImage.base64}`} // Handle the updated format
                                alt="Uploaded"
                                className="driver-popup-uploaded-image"
                                onClick={openFilePicker}
                                title="Click to replace image"
                            />
                            <button
                                className="driver-popup-remove-image"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUploadedImage(null);
                                }}
                                title="Remove image"
                            >
                                X
                            </button>
                        </div>
                    ) : (
                        <p>Drag & Drop an image here or click to select</p>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                </div>
                {uploadedImage && (
                    <p className="driver-popup-replace-text">
                        Only 1 picture allowed. Click or drag and drop a new image to replace the current uploaded image.
                    </p>
                )}
                {errorMessage && (
                    <p className="driver-popup-error">{errorMessage}</p>
                )}
                <button
                    className="driver-popup-button driver-popup-button-submit"
                    onClick={handleAddToCart}
                >
                    Add to Cart
                </button>
                <button className="driver-popup-button driver-popup-button-cancel" onClick={resetPopup}>
                    Cancel
                </button>
            </div>
        </div>
    ) : null;
};

export default SubmitAuthenticationPopup;
